import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { safeFetch } from '../utils/fetchJson';

function invalidateCaches(queryClient) {
  queryClient.invalidateQueries({ queryKey: ['incidents-list'] });
  queryClient.invalidateQueries({ queryKey: ['incidents-chart'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}

async function quietFetch(url) {
  try { await safeFetch(url); } catch {}
}

export default function useIncidentSocket(user) {
  const queryClient = useQueryClient();
  const modeRef = useRef('idle');
  const timersRef = useRef({ poll: null, retry: null, connect: null });
  const wsRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    const t = timersRef.current;
    if (t.poll) { clearTimeout(t.poll); t.poll = null; }
    if (t.retry) { clearTimeout(t.retry); t.retry = null; }
    if (t.connect) { clearTimeout(t.connect); t.connect = null; }
  }, []);

  const userId = user?.id || user?.username || !!user;
  useEffect(() => {
    if (!user) return;

    let destroyed = false;
    const host = window.location.host;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${host}/api/ws/incidents`;
    const pollFailsRef = { count: 0, interval: 15000 };

    function startPolling() {
      if (destroyed || modeRef.current === 'polling') return;

      modeRef.current = 'polling';
      pollFailsRef.count = 0;
      pollFailsRef.interval = 15000;
      console.debug('ws: polling started (15s)');

      invalidateCaches(queryClient);

      schedulePoll();

      scheduleRetry();
    }

    function schedulePoll() {
      if (destroyed || modeRef.current !== 'polling') return;
      timersRef.current.poll = setTimeout(() => {
        if (destroyed || modeRef.current !== 'polling') return;
        quietFetch('/api/incidents?limit=1')
          .then(() => {
            pollFailsRef.count = 0;
            pollFailsRef.interval = 15000;
          })
          .catch((err) => {
            pollFailsRef.count += 1;
            const status = err.status || 0;
            if (status === 524 || status >= 502) {
              pollFailsRef.interval = Math.min(pollFailsRef.interval * 3, 120000);
            } else {
              const backoff = [15000, 30000, 60000, 120000, 240000];
              pollFailsRef.interval = backoff[Math.min(pollFailsRef.count, backoff.length - 1)];
            }
            console.debug(`ws: poll fail #${pollFailsRef.count} (${status}), next in ${pollFailsRef.interval / 1000}s`);
          })
          .finally(() => {
            invalidateCaches(queryClient);
            schedulePoll();
          });
      }, pollFailsRef.interval);
    }

    function stopPolling() {
      modeRef.current = 'idle';
      pollFailsRef.count = 0;
      pollFailsRef.interval = 15000;
      if (timersRef.current.poll) {
        clearTimeout(timersRef.current.poll);
        timersRef.current.poll = null;
      }
      if (timersRef.current.retry) {
        clearTimeout(timersRef.current.retry);
        timersRef.current.retry = null;
      }
    }

    function scheduleRetry() {
      if (destroyed) return;
      timersRef.current.retry = setTimeout(() => {
        if (destroyed) return;
        if (modeRef.current === 'polling') {
          console.debug('ws: retrying...');
          connectWS();
        }
      }, 60000);
    }

    function connectWS() {
      if (destroyed) return;

      modeRef.current = 'connecting';

      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        startPolling();
        return;
      }

      let opened = false;
      let switched = false;

      const fallbackToPolling = () => {
        if (switched) return;
        switched = true;
        clearTimeout(timersRef.current.connect);
        if (!destroyed && !opened) startPolling();
      };

      timersRef.current.connect = setTimeout(() => {
        if (!opened && !destroyed) {
          console.debug('ws: timeout (15s), switching to polling');
          try { ws.close(); } catch {}
          fallbackToPolling();
        }
      }, 15000);

      let heartbeat = null;

      ws.onopen = () => {
        if (destroyed) return;
        opened = true;
        clearTimeout(timersRef.current.connect);
        timersRef.current.connect = null;
        stopPolling();
        modeRef.current = 'realtime';
        console.debug('ws: realtime active');
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'incident_created' || msg.type === 'incident_updated' || msg.type === 'incident_resolved') {
            invalidateCaches(queryClient);
          }
        } catch {}
      };

      ws.onclose = (event) => {
        if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
        if (destroyed) return;
        if (event.code >= 4000) return;
        if (!opened) {
          if (modeRef.current !== 'polling') startPolling();
          return;
        }
        if (opened) {
          modeRef.current = 'connecting';
          setTimeout(() => { if (!destroyed) connectWS(); }, 5000);
        }
      };

      ws.onerror = () => {
        fallbackToPolling();
      };

      wsRef.current = ws;
    }

    connectWS();

    return () => {
      destroyed = true;
      clearAllTimers();
      stopPolling();
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, [userId]);
}
